import NavBase from "./lib/nav_base";
import { PanesManager } from "./lib/panes_manager";
import Tracker from "../../lib/tracker";
import {NativeAppSignalEmitter} from "./lib/native_app_signal_emitter";
import { getTrackerOptions } from "../../lib/tracker_helpers";
import {
    getCurrentPaneOptionComponent,
    getPaneOptionComponentByIdentifier,
} from "./instances/state_machine/actions/helpers/pane_option_helpers";
import { primaryPaneOptionCollection } from "./instances/primary_pane_option_collection";
import {NavToggleComponent} from "./components/nav_toggle_component";
import {Version} from "../lib/version";

export const NavConfig = {
    bootedEventName: "OnlineAppNav-booted",
    toggledEventName: "OnlineAppNav-toggled",
    openEventName: "OnlineAppNav-open",
    closeEventName: "OnlineAppNav-close",
    configuredEventName: "OnlineAppNav-configured",
};

export class Nav extends NavBase {
    constructor(navRoot, props) {
        super(props);

        this.navRoot = navRoot;

        this.code = props.code;

        this.defaultMapFilterCode = props.default_map_filter_code; // The map filter to use when there have been no previously selected map filters (no persisted state).
        this.defaultNavOptionIdentifier = props.default_nav_option_identifier; // The nav option to use when the app is first loaded (no persisted state).

        this.isNativeApp = props.hasOwnProperty("is_native_app")
            ? props["is_native_app"]
            : null;

        this.nativeAppPlatform = props.hasOwnProperty("native_app_platform")
            ? props["native_app_platform"]
            : null;

        this.nativeAppVersion = Version.new(props["native_app_version"]);

        // The `NativeAppSignalEmitter` is used to communicate to the native apps via signals.
        if (props["native_app_signal_emitter_options"]) this.nativeAppSignalEmitter = new NativeAppSignalEmitter(props["native_app_signal_emitter_options"]);

        this.stateMachine.setPersistentStorageKey(
            `${this.code}__${this.stateMachine.persistentStorageKey}`,
        ); // We want a unique store for each online app.

        this.tracker = new Tracker(
            this.win,
            this.doc,
            getTrackerOptions(props.tracker_options),
        );

        this.navToggles = getNavToggleComponents(navRoot.element, this);

        // Initialize the panes manager and this constructs all the panes and pane options.
        this.panesManager = PanesManager.new(this);

        this.boot();

        // Fire a "booted" event to signal that this Nav has completed initialization.
        // The OnlineApp can listen and then complete it's boot process.
        document.dispatchEvent(new CustomEvent(NavConfig.bootedEventName, { detail: this }));
    }

    boot() {
        this.dispatchAction("bootingStarted");
        this.setInitialState();
        this.renderInitial();
        this.dispatchAction("bootingEnded");
    }

    setInitialState() {
        // Restore any state that might be persisted in the browser storage.
        this.dispatchAction("initFromPersistentStore");
    }

    renderInitial() {
        // Ensure the current pane is set and rendered.
        const pane = this.panesManager?.defaultPane
        if (pane) this.dispatchAction("openPane", pane);

        // Ensure the current pane option selected. This might open a secondary pane and change the current pane.
        const paneOptionComponent = this.getInitialPaneOptionComponent();
        if (paneOptionComponent) paneOptionComponent.fireClickEventOnLink();
    }

    rankersAnalyticsEventTracker(eventName, options) {
        if (!this.tracker || !this.tracker.eventTracker) return;
        console.log(
            `[Nav] rankersAnalyticsEventTracker(): eventName: ${eventName} `,
            options,
        );
        this.tracker.eventTracker(eventName, options);
    }

    getRootElement() {
        return this.getNavRoot()?.element;
    }

    getNavRoot() {
        return this.navRoot;
    }

    // Returns the pane option component for selection on boot.
    // The order of precedence is:
    //  - current option (from state)
    //  - the default option
    //  - fall back to the first in the collection
    getInitialPaneOptionComponent() {
        return (
            getCurrentPaneOptionComponent(this.stateMachine) ||
            getPaneOptionComponentByIdentifier(this.defaultNavOptionIdentifier) ||
            primaryPaneOptionCollection[0]
        );
    }

    // Returns a map filter code (which identifies a pane option).
    // Either the default map filter code or the first map filter code in the collection.
    getInitialMapFilterCode() {
        return (
            getPaneOptionComponentByIdentifier(this.defaultMapFilterCode)
                ?.mapFilterCode ||
            primaryPaneOptionCollection.find(
                (paneOption) => !!paneOption.mapFilterCode,
            )?.mapFilterCode
        );
    }

    // Ask the native app or OnlineApp to toggle the nav - shown or hidden within the user's view.
    // The OnlineAppNav instance isn't directly responsible for toggling itself or managing its toggled state.
    // Its current context might be within an OnlineApp instance (within a web browser) or a native app.
    // The Nav instance delegates toggling and state responsibility to the OnlineApp instance or the native app by letting them know that the nav should be toggled.
    // For the native app, it sends a URL to the native app to toggle the nav (via the NativeAppSignalEmitter instance).
    // For the OnlineApp it dispatches a custom event on the document to signal that the nav should be toggled.
    toggleNav() {
        if (this.isNativeApp && this.nativeAppSignalEmitter) {
            // The native apps will listen for this event and then handle the toggling of the nav.
            this.nativeAppSignalEmitter.emit(NavConfig.toggledEventName);
        } else {
            // The `OnlineAppNav` instance will listen for this event and then handle the toggling of the nav.
            document.dispatchEvent(new CustomEvent(NavConfig.toggledEventName, { detail: this }));
        }
    }

    // Ask the native app or the OnlineApp to open the nav.
    openNav() {
        if (this.isNativeApp && this.nativeAppSignalEmitter) {
            // The native apps will listen for this event and then handle the opening of the nav.
            this.nativeAppSignalEmitter.emit(NavConfig.openEventName);
        } else {
            // The `OnlineAppNav` instance will listen for this event and then handle the opening of the nav.
            document.dispatchEvent(new CustomEvent(NavConfig.openEventName, { detail: this }));
        }
    }

    // Ask the native app or the OnlineApp to close the nav.
    closeNav() {
        if (this.isNativeApp && this.nativeAppSignalEmitter) {
            // The native apps will listen for this event and then handle the closing of the nav.
            this.nativeAppSignalEmitter.emit(NavConfig.closeEventName);
        } else {
            // The `OnlineAppNav` instance will listen for this event and then handle the closing of the nav.
            document.dispatchEvent(new CustomEvent(NavConfig.closeEventName, { detail: this }));
        }
    }

}

function getNavToggleComponents(parentElement, nav) {
    if (!parentElement) return [];
    return Array.from(parentElement.querySelectorAll(NavToggleComponent.config.selector)).map((element) => {
        return NavToggleComponent.new(element, nav);
    });
}

